<template>
  <div class="product-aside-cart-container">
    <slot v-if="!disabledButton"></slot>
    <div class="product-aside-cart-item">
      <form @submit.prevent>
        <fieldset v-if="hasVariants" aria-labelledby="variant-label">
          <legend id="variant-label" class="sr-only">Product variant</legend>
          <VariantDropdown
            :variants="product.product_variants"
            :hasVariants="hasVariants"
            :variantStock="variantStock"
            :selectedVariant="selectedVariantRecord"
            @variant-selected="set"
          />
        </fieldset>
        <div class="quantity-controls" :style="{display: 'flex', flexWrap: 'wrap', flexGrow: '1', flexDirection: 'row', gap: '5px', marginBottom: '5px' }" role="group" aria-label="Quantity selector">
          <button
            :disabled="disabledButton || disableSubtraction || cart_has_max_items_already"
            type="button"
            class="cart-small product-aside-cart-down"
            @click="decrease"
            aria-label="Decrease quantity"
          >-</button>
          <input
            v-model="count"
            @input="count = Math.min(count, max)"
            type="number"
            min="1"
            :max="max"
            aria-label="Product quantity"
            :aria-valuemin="1"
            :aria-valuemax="max"
            :aria-valuenow="count"
            class="product-aside-cart-input"
          />
          <button
            :disabled="disabledButton || disableAddition || cart_has_max_items_already"
            type="button"
            class="cart-small product-aside-cart-up"
            aria-label="Increase quantity"
            @click="increase"
          >+</button>
          <button
            :disabled="disabledButton || cart_has_max_items_already"
            type="submit"
            data-test="addToCartButton"
            class="btn--product-aside"
            :class="{ 'btn--product-aside-disabled': addingToCart }"
            @click="addToCart"
            :aria-label="cartLabel"
          >{{ cartLabel }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import events from "Assets/js/components/events";
import VariantDropdown from "../Picker/index.vue";
import { mapGetters } from "vuex";
import cartContent from "Templates/content/cart.json";

export default {
  name: "AddToCart",
  components: { VariantDropdown },
  emits: ["variant-selected"],
  props: {
    product: Object,
    variantStock: Array,
  },
  data() {
    return {
      count: 1,
      max: 99,
      disableSubtraction: false,
      disableAddition: false,
      disabledButton: false,
      addingToCart: false,
      selectedVariant: this.product.product_variants.find((pv) => pv.availableForSale)?.id || this.product.product_variants[0]?.id,
      hasVariants: this.product.product_variants.length > 1,
      cart_has_max_items_already: false,
    };
  },
  watch: {
    selectedVariant: {
      immediate: true,
      handler(newVariant) {
        if (!newVariant) {
          return;
        }
        this.$emit("variant-selected", newVariant);
      }
    }
  },
  computed: {
    ...mapGetters(["cartInstance", "isB2B"]),
    cartLabel() {
      if (this.cart_has_max_items_already) {
        return cartContent.product.has_max_in_cart_already;
      }

      return this.addingToCart ? cartContent.product.adding : cartContent.product.add_to_bag;
    },
    selectedVariantRecord() {
      return (this.product.product_variants || []).find((variant) => variant.id === this.selectedVariant) || null;
    }
  },
  beforeMount() {
    if (this.product.shopify_metafields?.purchase_limit && this.product.shopify_metafields?.purchase_limit >= 1) {
      this.max = parseInt(this.product.shopify_metafields.purchase_limit) - this.cartInstance.findCartQuantityTotalByProduct(this.product);
      if (this.max < 0) {
        this.max = 0;
      }
      this.checkPurchaseLimit();

      window.addEventListener(
        'cartService:cartFetched',
        () => {
          this.cart_has_max_items_already = this.cartInstance.doesCartHaveMaxQuantityOfProduct(this.product);
          this.max = parseInt(this.product.shopify_metafields.purchase_limit) - this.cartInstance.findCartQuantityTotalByProduct(this.product);
          if (this.max < 0) {
            this.max = 0;
          }
        }
      );
    }
  },
  methods: {
    set: function (variant) {
      if (variant.availableForSale) {
        this.selectedVariant = variant.id
        this.dropdownTitle = variant.title
        if(variant.title !== 'Choose An Option'){
          this.disabledButton = false;
          this.productOutOfStock = false;
        }
      } else {
        this.disabledButton = true;
        this.productOutOfStock = true;
      }
    },
    increase() {
      if (this.count < this.max) this.count++;
      this.checkPurchaseLimit();
    },
    decrease() {
      if (this.count > 1) this.count--;
      this.checkPurchaseLimit();
    },
    checkPurchaseLimit() {
      if(this.count >= this.product.shopify_metafields?.purchase_limit){
        this.disableAddition = true;
      } else {
        this.disableAddition = false;
      }
      this.disableSubtraction = this.count <= 1;
    },
    async addToCart() {
      if (this.count === 0) return;
      this.addingToCart = true;
      this.disabledButton = true;
      try {
        const res = await this.cartInstance.addToCart(
          this.selectedVariant,
          this.count,
          this.product.shopify_id
        );
        this.addingToCart = false;
        this.disabledButton = false;
        events.cartChange();
        this.count = 1;
        if (this.isB2B) events.cartShow();
        return res;
      } catch (e) {
        this.addingToCart = false;
        this.disabledButton = false;
        throw e;
      }
    }
  },
  mounted() {
    this.cart_has_max_items_already = this.cartInstance.doesCartHaveMaxQuantityOfProduct(this.product);
    // todo: Not sure what this is for (ecrm?), might be dead.
    window.dispatchEvent(new CustomEvent("product_page_visit_tracker", { detail: { product_id: this.id } }));
  },
};
</script>
